import type * as THREE from 'three/webgpu';
import type { Node } from 'three/webgpu';
// R5: the only VALUE imports this file needs from three, and both are for the static split's
// placeholder light — an `Object3D` posed by hand, exactly the shape `CSMShadowNode`'s own
// `LwLight` takes. `three/webgpu` is already on this module's runtime chain via the CSM import.
import { Object3D, Vector3 } from 'three/webgpu';
import { CSMShadowNode } from 'three/examples/jsm/csm/CSMShadowNode.js';
import {
  float,
  min,
  mix,
  nodeObject,
  positionView,
  reference,
  renderGroup,
  shadow as shadowNodeFactory,
  uniform,
  viewZToOrthographicDepth,
} from 'three/tsl';
import {
  FWG_DYNAMIC_SHADOW_LAYER,
  FWG_STATIC_SHADOW_LAYER,
  FWG_DRIVEN_SHADOW_MAPS,
  SUN_HANDOVER_FRACTION,
  cascadeBreaks,
  cascadeCameraFarM,
  shadowRefreshDue,
  sunHandoverBandM,
  type AppliedShadowUpdate,
  type SunShadowPolicy,
  type SunShadowSplit,
} from './lightModel';
/*
 * ⭐ B6 — THE SUN'S SHADOW SCHEDULE USES THE RIG'S OWN APPLIER, NOT A COPY OF IT.
 *
 * `applyShadowUpdatePolicy` is the ONE function that says what an arm DOES to a `LightShadow`
 * (`autoUpdate`, `camera.layers`, and the `set`-not-`disable` argument about r185's inherit
 * branch); `enableStaticShadowLayer` is the opt-IN half of `FWG_STATIC_SHADOW_LAYER` without which
 * a frozen map renders empty. Both live in `worldLights.ts` because that is where the meanings
 * were established, and this file imports them rather than re-deriving them — a second answer to
 * "how do I freeze a shadow map" living beside the sun is the exact shape that kept the sun
 * outside every light mechanism for its whole life.
 *
 * No cycle: `worldLights.ts` names no CSM module. Both modules are already static-imported by
 * `scene.ts`, so the bundle is unmoved.
 */
import {
  applyShadowUpdatePolicy,
  enableDynamicShadowLayer,
  enableStaticShadowLayer,
} from './worldLights';

/**
 * ── ⭐ B3 · THE SUN'S CASCADES, ATTACHED THROUGH THE ONE HOOK THREE OFFERS ─────────────────────
 *
 * `lightModel.sunShadowPolicy` is the TABLE (pure, per tier); this is the mechanism. It owns one
 * `CSMShadowNode` on the world's sun and nothing else, and every line of it exists because of a
 * property of three r185 verified in `node_modules` and quoted here:
 *
 *  1. THE ATTACHMENT IS A FIELD, READ AT LIGHT-NODE BUILD. `AnalyticLightNode.setupShadow`:
 *         const customShadowNode = this.light.shadow.shadowNode;
 *         if ( customShadowNode !== undefined ) { shadowNode = nodeObject( customShadowNode ); }
 *         else { shadowNode = this.setupShadowNode(); }
 *     So `sun.shadow.shadowNode = node` replaces the stock map — the sun's own map is never built.
 *     Nothing in a material changes: `ShadowBaseNode.setupShadowPosition` reads the material's
 *     `receivedShadowPositionNode || positionWorld`, and the fetch is composed inside the light's
 *     own shadow term. Factory materials, classic `MeshStandardMaterial` and glTF materials all
 *     receive, exactly as they did from the single map.
 *
 *  2. THE CASCADES ARE NOT LIGHTS. Per cascade the node makes an `LwLight extends Object3D` with
 *     `castShadow = true` and `shadow = light.shadow.clone()`. It is not `isLight`, so
 *     `Renderer._projectObject` never pushes it and `LightsNode.customCacheKey()` (which hashes
 *     `light.id` and `light.castShadow` per light into every material's dynamic key) sees ONE sun,
 *     as before. Adding cascades moves no pipeline — `tests/renderCsm.test.ts` gathers the key's
 *     inputs across a camera move and asserts they are identical.
 *
 *  3. THE PER-FRAME WORK IS POSE. `updateBefore` re-fits each cascade's ortho box to the camera
 *     frustum slice in light space and snaps the centre to a texel (`Math.floor( _center.x /
 *     texelWidth ) * texelWidth`) — writes to `lwLight.position`, `shadowCam.left/right/top/
 *     bottom`. All of it is in `LIGHT_KEY_FREE_FIELDS`' class. `updateFrustums()` (breaks + slice
 *     bounds) is JS that must re-run when the camera's projection changes; `tick()` compares the
 *     four projection inputs each frame and re-runs it on a change, which covers a resize AND the
 *     speed FOV kick without a second hook in `scene.ts`.
 *
 *  4. ⚠️ THE MATRIX POLICY WOULD SILENTLY BREAK IT. `scene.ts` sets
 *     `Object3D.DEFAULT_MATRIX_AUTO_UPDATE = false` at module scope, so the `LwLight`s and their
 *     targets — plain `Object3D`s the node constructs — would never compose the pose `updateBefore`
 *     writes, and every cascade camera would sit at the origin. Shadows "from nowhere", no error.
 *     `attach()` opts each cascade light, its target and its shadow camera in, the same way
 *     `applySun` opts the sun's own shadow camera in.
 *
 *  5. THE NODE INITIALISES LAZILY, ON THE FIRST MATERIAL BUILD. `setup( builder )` is
 *     `if ( this.camera === null ) this._init( builder )`, binding `builder.camera` — whichever
 *     camera built the first material. `_init( { camera, renderer } )` reads exactly two renderer
 *     fields (`coordinateSystem`, `reversedDepthBuffer`), both known before the first frame, so
 *     `attach()` calls it EAGERLY with the game camera. That is a reach into a `@private` method,
 *     taken on purpose and pinned against the r185 source by the test: it turns "which camera did
 *     the cascades bind to" from a frame-1 surprise into a construction-time assertion
 *     (`state().cameraBound`), and it makes the cascade objects exist from the moment the policy
 *     is applied, so per-cascade map sizes are written once rather than polled for.
 *
 *  6. ⚠️ THE BAKE MUST NOT RUN UNDER CASCADES. They are fitted to the GAME camera's frustum, and
 *     outside every cascade the term is LIT (`ret` stays `vec4( 1 )`). A probe capture 40 m from
 *     the frozen camera would photograph the sun bleeding through walls — an atlas of a different
 *     room, with every downstream check green. And `probeGrid.freezeSceneForBake` freezes maps by
 *     `light.isLight && light.castShadow && light.shadow`, which the cascade objects fail, so their
 *     three depth passes would re-render on every one of the bake's ~500,000 capture renders.
 *     `detachForBake` puts the stock map back for the bracket and its restore puts the cascades
 *     back after — see the function for why that costs a `castShadow` cycle with a render inside.
 *
 *  7. ⭐ **THE SLOT TAKES A COMPOSITION, NOT ONLY A `ShadowBaseNode` — WHICH IS HOW THE BAKED SUN
 *     VISIBILITY GETS IN (`axon.probeVolume/5`).** `setupShadow` above wraps whatever it finds in
 *     `nodeObject()` and uses it as `this.colorNode.mul( shadowNode )`; it never checks
 *     `isShadowBaseNode` (grepped across `nodes/lighting/**` and `renderers/common/**`: the only
 *     reads are `ShadowBaseNode`'s own constructor and an unrelated comment). So a plain TSL node
 *     is legal there, and `setDistantShadowTerm` puts `csm.mul( mix( 1, V, w ) )` in the slot when
 *     a probe volume carries a baked term. See that function for the whole argument; the two
 *     properties that make it safe are:
 *       - **`updateBefore` still fires.** Registration is by BUILD TRAVERSAL, not by the slot:
 *         `Node.build`'s setup stage calls `builder.addSequentialNode( this )` (Node.js:948) for
 *         every node it builds and `NodeBuilder.buildUpdateNodes` (NodeBuilder.js:895-905) pushes
 *         every sequential node with a non-NONE `updateBeforeType` into `updateBeforeNodes`. The
 *         `CSMShadowNode` is ALREADY reached as an operand of a `mul` today (line 230 above), so
 *         one more `mul` around it is the same class of thing, not a new one. Pinned by a 60-frame
 *         camera walk with the composed node attached (`tests/renderCsm.test.ts`).
 *       - **ownership does not move.** three disposes only the node it holds, which under a wrapper
 *         is the wrapper (`setup`'s `castShadow === false` arm, AnalyticLightNode.js:267-270), and
 *         `CSMShadowNode.dispose()` (CSMShadowNode.js:573) is what un-parents the cascade
 *         `LwLight`s. `detachNow()` below has always called it and still does, on every swap path.
 *
 * ── CACHE-KEY DISCIPLINE (L2's, extended) ───────────────────────────────────────────────────────
 *
 * FREE: everything in 3 above; `maxFar`; per-cascade `mapSize`/`camera.far`; `updateFrustums()`.
 * KEY: `sun.castShadow` (the ONE existing key move, `LIGHT_KEY_MOVING_FIELDS`); attach/detach of
 * `shadow.shadowNode` — which takes effect ONLY through a `castShadow` cycle, because
 * `LightsNode.setupLightsNode` keeps one `AnalyticLightNode` per light in a module-level WeakMap
 * for the page's life and that node caches its `shadowColorNode` until a build with
 * `castShadow === false` disposes it; the cascade COUNT and `fade` (baked into the node graph at
 * `setup`). Every `castShadow` write below carries `FWG_LIGHT_TOGGLE_OK` and its reason.
 */

/** The two renderer fields `CSMShadowNode._init` reads. Narrow so a test can pass a literal. */
export interface CsmRendererLike {
  readonly coordinateSystem: number;
  readonly reversedDepthBuffer: boolean;
}

export interface CsmRigOptions {
  sun: THREE.DirectionalLight;
  camera: THREE.PerspectiveCamera;
  renderer: CsmRendererLike;
  policy: SunShadowPolicy;
  /** The authored `sun.castShadow` — the rig owns the sun's flag while it cycles it. */
  castShadow: boolean;
  /**
   * ⭐ B6 — THE ROOT `staticOnly` WALKS, i.e. the scene. REQUIRED rather than defaulted from
   * `sun.parent`, because the one thing worse than a frozen sun shadow is a frozen sun shadow over
   * an EMPTY static layer: it renders black-on-nothing and reads as "the sun stopped casting",
   * with every state readout green. An option that can be forgotten is one that will be.
   */
  staticRoot: THREE.Object3D;
}

/** Published through `__fwg.state().shadows.csm` — state, so a screenshot is not the evidence. */
export interface CsmState {
  enabled: boolean;
  attached: boolean;
  cascades: number;
  /**
   * ⭐ THE CASCADE COUNT THE GRAPH ACTUALLY HOLDS (`node.lights.length`), beside `cascades`, which
   * is the POLICY's number. They agree in every healthy state and their disagreement is the only
   * observable of a swap that half-happened — which is a real defect this rig had until a
   * measurement caught the frame rendering two cascade passes while `cascades` reported three.
   * `0` while detached (a bake bracket, mid-swap, `?csm=0`), which is honest rather than a gap.
   */
  cascadesAttached: number;
  distanceM: number;
  breaksM: number[];
  mapSizes: number[];
  fade: boolean;
  /** `node.camera === the game camera` — the frame-1 trap of point 5, as a boolean. */
  cameraBound: boolean;
  /** Cascade lights present in the scene graph (parented lazily by `updateBefore`). */
  lightsParented: number;
  /** Per-cascade sizes written onto the clones — `attach()` did its work. */
  applied: boolean;
  /** A count/fade change is waiting for the two-frame rebuild. */
  pendingAttach: boolean;
  /** `updateFrustums()` runs since attach — 1 at boot, +1 per projection change. */
  frustumRefits: number;
  /** `castShadow` cycles this rig has spent (bake detach + restore, count/fade swaps). */
  keyCycles: number;
  /**
   * ⭐ WHICH SHADOW TERM THE SUN IS ACTUALLY CARRYING BEYOND THE LAST CASCADE, as a
   * CONSTRUCTION-TIME FACT rather than as an intention. `'probe'` ⇒ the slot holds
   * `csm.mul(mix(1, V, w))` and distant geometry is shadowed by the baked per-probe term;
   * `'none'` ⇒ the slot holds the bare `CSMShadowNode` and the emitted WGSL is byte-identical to
   * the build before this feature existed.
   *
   * A session that thinks it has the feature and does not says `'none'` here — which is the whole
   * reason this is published. `'none'` is the arm that ships most often: `?gi=off`, `indirect:
   * 'fill'`, a refused atlas, tier 0, the WebGL2 fallback, an atlas whose header says
   * `sunVisibility.baked: false`, and every frame before the async bind completes.
   */
  distantTerm: 'none' | 'probe';
  /** `sunHandoverBandM(policy)` — where the cascades hand over, metres. `[0, 0]` under `?csm=0`. */
  handoverBandM: [number, number];
  /**
   * ⭐ B6 — the sun's shadow SCHEDULE and the maps it is applied to, as a fact about the graph.
   * `shadowMapsDriven` is how many `LightShadow`s this rig currently schedules by hand (0 while
   * the arm is `everyFrame`), read off the objects it wrote — the R1.6-D discipline that a
   * self-report proves nothing. It is `1` under a frozen or single-map sun and `cascades` under an
   * `everyNFrames` cascade rig; a number that disagrees with `cascades` is the partially-frozen
   * cascade set this design refuses to be able to produce.
   */
  shadowUpdate: AppliedShadowUpdate;
  shadowUpdateN: number;
  shadowMapsDriven: number;
  /**
   * ⭐ R5 — the two-map split, READ OFF THE GRAPH. Every field here is a fact about objects this
   * rig wrote, not about the policy it was handed: a split that is configured and not reaching the
   * frame is this repo's signature failure, and its failure mode is a PICTURE (an empty static map
   * reads as "the sun stopped casting"), so a self-report would prove nothing.
   */
  split: {
    /** What the policy asked for. */
    requested: boolean;
    /** The static `ShadowNode` exists and is in the composed graph. */
    attached: boolean;
    /** `sharedCascades` — cascades still rendering BOTH channels. */
    sharedCascades: number;
    /** Cascade shadow cameras whose mask is the dynamic channel — counted off the cameras. */
    dynamicCascades: number;
    /** Objects `enableDynamicShadowLayer` opted in at the last walk. 0 is the empty-map bug. */
    dynamicObjects: number;
    /**
     * ⭐ THE REACH THE FROZEN MAP OWNS, IN METRES — `frozenFromM` out to `staticRadiusM`, with the
     * live cascades nearer than `frozenFromM`. Published because the owner's objection to
     * precomputed shadows is that they cover the whole scene, and the answer has to be a number he
     * can read off a running frame rather than a claim. `staticRadiusM` is
     * `shadowDistanceM + refitM` and reads NOTHING about the level's extent.
     */
    frozenFromM: number;
    staticRadiusM: number;
    staticCentre: [number, number, number];
    /** World metres per static-map texel — the quality number the trade is about. */
    texelM: number;
    /** Full static passes rendered this session. 1 while standing still; +1 per refit step. */
    refits: number;
    /** The static camera's layer mask. Must be `1 << FWG_STATIC_SHADOW_LAYER`. */
    staticLayerMask: number;
    /**
     * ⭐ THE THREE FIELDS THAT SAY WHETHER THE MAP IS ACTUALLY FROZEN, and they exist because the
     * first measured build was NOT. `ShadowNode.updateBefore` re-renders whenever
     * `shadow.needsUpdate || shadow.autoUpdate` (`ShadowNode.js:855`) and clears `needsUpdate` only
     * when `shadowMap.depthTexture.version` has not moved since `updateShadow` latched it
     * (`:873-877`) — so anything that reallocates the map leaves `needsUpdate` LATCHED TRUE and the
     * "frozen" pass runs every frame, at full price, for ever. `refits` counts what this rig
     * INTENDED; `staticNeedsUpdate` is what the renderer will actually do next frame. When they
     * disagree, the second one is right.
     */
    staticMapPx: number;
    staticAutoUpdate: boolean;
    staticNeedsUpdate: boolean;
  };
}

export interface CsmRig {
  policy(): SunShadowPolicy;
  /**
   * Once per rendered frame, before the draw. Re-fits on a projection change and completes a
   * pending two-frame swap. Pose/uniform work only — asserted, never assumed.
   */
  tick(frameIndex: number): void;
  /**
   * The editor's apply. Distance and map sizes are free writes; a cascade-count or fade change is a
   * node-graph change and is scheduled: detach now, so the next frame builds without the sun's
   * shadow term, then attach on the frame after — the `castShadow` cycle, spread over two frames
   * instead of blocking an editor event on a full-scene rebuild.
   */
  apply(policy: SunShadowPolicy, castShadow: boolean): void;
  /**
   * The bake bracket. Puts the stock single map (`applySun`'s ±extent box) back for the duration,
   * running `renderOnce` between the two halves of the `castShadow` cycle so the light node
   * actually rebuilds (point 6 above). Returns the restore; call it in the bracket's `finally`.
   */
  detachForBake(renderOnce: () => void): () => void;
  /**
   * ⭐ INSTALL (or remove) THE BAKED PER-PROBE SUN-VISIBILITY TERM. See the implementation for the
   * full argument; the contract is: an OPAQUE `Node<'float'>` in `[0, 1]`, or `null`. csmRig never
   * learns where it came from, and `render/gi/**` is never named here — the node is built by
   * `render/gi/sunVisibilityNode.ts` and carried across by `scene.ts`'s `bindProbeVolume`, which
   * already owns the volume's life.
   */
  setDistantShadowTerm(term: Node<'float'> | null): void;
  /**
   * ⭐ B6 — "the world changed, so a frozen sun map is a picture of a level that is gone."
   *
   * The sun's half of `worldLights.refreshCachedShadows()`, and it is a SEPARATE call for the same
   * reason that one is separate from `apply`: the scene graph GROWS after the rig is built
   * (`static_mesh` decor is a 50 MB glTF that lands seconds later; registry entities spawn on
   * demand), so the static-layer walk has to be re-run at the moments something knows the world
   * moved — never per frame, and never once at construction. Returns the number of maps rescheduled.
   */
  refreshSunShadow(): number;
  state(): CsmState;
  dispose(): void;
}

/** What `_init` needs — `@private` in r185 but load-bearing here; pinned against the source. */
interface CsmNodeInternals {
  _init(builder: { camera: THREE.Camera; renderer: CsmRendererLike }): void;
}

interface ShadowWithNode {
  shadowNode?: unknown;
}

/**
 * ── ⭐ THE COMPOSITION — `csm · mix(1, V, w)`, TWO ALU OPS, NO BRANCH ────────────────────────────
 *
 * A MULTIPLY, not a select, and the reason it needs no seam handling is arithmetic rather than
 * tuning:
 *
 *   - **Inside the cascades** `w = 0`, so `mix(1, V, 0) = 1` and the value is BYTE-IDENTICAL to
 *     the bare `CSMShadowNode`'s. Nothing this feature does can darken a near shadow.
 *   - **Beyond them** `CSMShadowNode`'s `ret` never leaves `vec4(1)` (both arms — `_setupFade`
 *     line 393 and `_setupStandard` line 473 initialise it to `vec4(1,1,1,1)` and only assign
 *     inside an `If( inRange )`), so the value is exactly `V`. That is the owner's defect fixed.
 *   - **In the band** both ramps are linear in the SAME `linearDepth`, so they REGISTER rather
 *     than merely overlapping. Past the last cascade's centre three's `ratio` is
 *     `min(ld − csmX, 1 − ld) / 0.25`, and `1 − ld` is the binding term there (at tier 2
 *     `csmX ≈ 0.186`, so `ld − csmX ≥ 0.56` across the whole band), giving `ratio = 4(1 − ld)`.
 *     Ours is `w = 4(ld − 0.75)`, i.e. **exactly `1 − ratio`** — so the composed value is
 *     `(1 − (1−S)·ratio) · (ratio + (1−ratio)·V)`, which is `S` at `ld = 0.75` and `V` at `ld = 1`
 *     with no discontinuity at either end.
 *
 * ── ⚠️ THE RAMPS REGISTER; THE TERMS DO NOT COMPOSE. A MEASURED DEFECT, NAMED AND NOT TAKEN ─────
 *
 * Everything above about the two ramps is TRUE and is re-asserted in
 * `tests/renderGiSunVisibility.test.ts`: `w + ratio === 1` across the whole band, so there is no
 * DEPTH GAP — no region where the cascade has faded out and the probe term has not faded in.
 *
 * It does not follow that the composed VALUE is right, and it is not. A handover between two
 * shadow authorities should be a MIX — `mix(S, V, w)` — and a product of two partially-faded
 * shadows is not one. Substituting three's own fade, `csm = w + S·(1 − w)`:
 *
 *     csm · mix(1, V, w)  −  mix(S, V, w)  =  w·(1 − w)·(1 − S)·(1 − V)
 *
 * — zero at both ends, and POSITIVE in between, i.e. LIGHTER. Measured (that same file, the closed
 * form asserted term by term): the worst case is **0.25 at `w = 0.5`**, `ld = 0.875` — 78.75 m at
 * tier 2 — where the cascade and the probes BOTH say fully shadowed and the frame gets a quarter
 * of the sun anyway, over a 22.5 m deep band. It is a candidate for the owner's *"we still get our
 * 'no shadows' area"*, and it is a real defect regardless of whether it is the one he means.
 *
 * ⭐ **THE EXIT, DERIVED AND PINNED BUT NOT APPLIED — `saturate( csm − w·(1 − V) )`.** Expand it:
 * `w + S(1−w) − w + wV = S(1−w) + wV = mix(S, V, w)`, EXACTLY, with no singularity at `w = 1`
 * (where `csm` is three's `vec4(1)` and the value is `V`), the same two ALU ops, no branch and no
 * extra fetch. `tests/renderGiSunVisibility.test.ts` asserts that identity across the band so it
 * cannot rot while it waits.
 *
 * ⛔ **WHY IT IS NOT TAKEN HERE.** It is correct only under `fade: true`. Under `_setupStandard`
 * (tiers 0/1) `csm` IS `S`, unfaded, and the right expression there is the plain `mix(csm, V, w)`;
 * the subtraction would over-darken by `w·(1 − S)·(1 − V)` and needs the `saturate` merely to stop
 * going negative. So taking it FORKS THE COMPOSITION PER TIER — a JS-time fork, cheap and
 * branchless, but a second author of the sun's shadow term introduced in the same change as the
 * band-7 fix, which is how the next agent inherits an ambiguity. It also reverses a ⭐-pinned
 * ruling in `CLAUDE.md` (*"the two ramps register"* — the ramps do; the terms do not), and that
 * belongs to the owner rather than to a build agent. One look at a real frame between 67.5 and
 * 90 m tells him whether the lighter band is what he is seeing.
 *
 * ⚠️ **`linearDepth` IS REBUILT HERE RATHER THAN SHARED, AND THE TWO CANNOT DRIFT BECAUSE THEY
 * READ THE SAME TWO FIELDS.** `CSMShadowNode`'s copy is a local inside a `@private` method
 * (`_setupFade`, CSMShadowNode.js:380-384) — there is no handle to reuse. The depth expression
 * below is a transcription of those five lines, `.setName()` aside, with the same `renderGroup`, the same
 * `reference('camera.near', …)` on the same node, and the same `Math.min( maxFar, camera.far )` in
 * the same `onRenderUpdate`. `maxFar` is written by `attachNow`/`apply` from
 * `Math.min(policy.distanceM, camera.far)`, so there is ONE writer of the number both ramps read.
 *
 * ⚠️ **TSL PROVENANCE (hard rule 3).** `docs/ai/tsl-patterns.ts` has no shadow-composition pattern,
 * so every line is cited above against r185's own source. Nothing novel is invented: the depth
 * expression is three's, and the composition itself is `mul`/`mix`/`clamp`. It is NOT added to
 * `tsl-patterns.ts` — that file's bar is "verified, runnable", and this graph has not been on a GPU.
 * The headless fences are three, and between them they cover everything except whether the WGSL
 * compiles: the node-graph census in `tests/renderSparseAddress.test.ts` (the term is 1 load +
 * 1 sample); the OBJECT IDENTITY of the `term === null` arm with a bare `CSMShadowNode` and the
 * reachability of the CSM node through the wrapper, in `tests/renderCsm.test.ts`; and a CPU
 * evaluation of this exact expression at a spread of depths in `tests/renderGiSunVisibility.test.ts`
 * — which is where the design's claim about the no-fade tiers was measured and CORRECTED.
 */
/**
 * The TSL `.d.ts` is loose about fluent chaining off `reference`/`uniform`/`nodeObject` — the same
 * looseness `sparseFetch.ts` casts through for its own handles. A narrow structural type at the
 * seam, never `any`, so the arithmetic below still typechecks as the scalars it compiles to.
 */
type TslFloat = {
  setGroup(group: unknown): TslFloat;
  onRenderUpdate(cb: () => number): TslFloat;
  sub(v: unknown): TslFloat;
  div(v: number): TslFloat;
  clamp(lo: number, hi: number): TslFloat;
  mul(v: unknown): TslFloat;
  /** `1 − x`, the TSL spelling. Used by the fade-on handover to build `w · (1 − V)`. */
  oneMinus(): TslFloat;
};

function composeDistantShadow(
  csm: CSMShadowNode,
  term: Node<'float'>,
  camera: THREE.PerspectiveCamera,
  /** `SunShadowPolicy.fade` — which of r185's two fade implementations `csm` came from. */
  fade: boolean,
): unknown {
  // CSMShadowNode.js:380 — `reference( 'camera.near', 'float', this )`, on the node's own camera.
  const cameraNear = (
    reference('camera.near', 'float', csm) as unknown as TslFloat
  ).setGroup(renderGroup);
  // CSMShadowNode.js:383-384 — the same uniform, the same update, the same two fields.
  const shadowFar = (uniform('float') as unknown as TslFloat)
    .setGroup(renderGroup)
    .onRenderUpdate(() => Math.min(csm.maxFar, camera.far));
  const linearDepth = viewZToOrthographicDepth(
    positionView.z,
    cameraNear as unknown as Parameters<typeof viewZToOrthographicDepth>[1],
    shadowFar as unknown as Parameters<typeof viewZToOrthographicDepth>[2],
  ) as unknown as TslFloat;
  const w = linearDepth
    .sub(SUN_HANDOVER_FRACTION)
    .div(1 - SUN_HANDOVER_FRACTION)
    .clamp(0, 1);
  /*
   * ── ⭐ A HANDOVER IS A MIX, NOT A PRODUCT — AND ONE RULE, TWO ADAPTERS ──────────────────────────
   *
   * The rule is `mix(S, V, w)`: at `w = 0` the cascade's own shadow `S`, at `w = 1` the baked probe
   * visibility `V`, linear between. What differs per tier is not the rule — it is how `S` is
   * RECOVERED from what `CSMShadowNode` hands back, because r185 has two fade implementations.
   * That is why this is not "two competing systems"; it is one handover with two adapters, chosen
   * ONCE at node-build time, so the fragment pays two ALU ops either way and no branch.
   *
   * ⚠️ WHAT WAS HERE BEFORE WAS `csm · mix(1, V, w)`, A PRODUCT OF TWO PARTIALLY-FADED SHADOWS, AND
   * IT BRIGHTENED THE MIDDLE OF THE BAND. Writing three's own fade out (`csm = w + S(1 − w)`), the
   * product expands to `[w + S(1−w)]·[(1−w) + V·w]`, which equals `mix(S, V, w)` only at the two
   * ends; in between the excess is exactly `w(1−w)(1−S)(1−V)`. Worst case `S = V = 0` at `w = 0.5`
   * (ld 0.875, 78.75 m at tier 2): a wall BOTH shadow authorities call fully dark is handed a
   * QUARTER of the sun, across a 22.5 m deep band. That is the owner's reported "we still get our
   * 'no shadows' area", and `tests/renderGiSunVisibility.test.ts` pins the closed form to 12 dp.
   *
   * FADE ON (tier 2, `_setupFade`): `csm = w + S(1 − w)`, so `csm − w(1 − V)` = `S(1−w) + wV` =
   * `mix(S, V, w)` EXACTLY — algebraically, with no division and therefore no singularity at
   * `w = 1`, which is why this form is preferred over recovering `S = (csm − w)/(1 − w)`.
   * FADE OFF (tiers 0/1, `_setupStandard`): `csm` IS `S` inside the single cascade, so the rule is
   * spelled directly as `mix(csm, V, w)`. Using the fade form here would darken by `w(1 − S)`.
   */
  const wNode = w as unknown as Node<'float'>;
  const csmNode = nodeObject(csm) as unknown as TslFloat;
  return fade
    ? csmNode
        .sub((wNode as unknown as TslFloat).mul((term as unknown as TslFloat).oneMinus()))
        .clamp(0, 1)
    : mix(csmNode as unknown as Node<'float'>, term, wNode);
}

/**
 * ── ⭐ R5 · THE STATIC HALF — ONE MAP, ONE PLACEHOLDER LIGHT, POSED BY HAND ─────────────────────
 *
 * `shadow( light, lightShadow )` is a public TSL factory (`ShadowNode.js:909`) and the node it
 * returns renders its OWN map in `updateBefore`, gated on `shadow.needsUpdate || shadow.autoUpdate`
 * (`ShadowNode.js:855`). That is exactly what a frozen static map needs, and it is precisely how
 * `CSMShadowNode` builds each cascade (`_init`: an `LwLight extends Object3D` with `castShadow`,
 * a `light.shadow.clone()`, and `shadow( lwLight, lShadow )`).
 *
 * ⚠️ THE LIGHT HAS TO BE OURS, NOT THE SUN. `LightShadow.updateMatrices( light )` poses the shadow
 * camera AT `light.matrixWorld`'s position looking at `light.target.matrixWorld`
 * (`LightShadow.js:206-210`) — there is no offset argument. Handing it the real sun would anchor
 * the box on the sun's authored stand-off (23 m from the origin on `mars-hangar-01`), which is
 * neither where the level is nor where the camera is. A placeholder `Object3D` is the only way to
 * put the box where the box needs to be.
 *
 * ⚠️ AND IT IS NOT A LIGHT, WHICH IS WHAT KEEPS IT FREE. `Renderer._projectObject` pushes only
 * `isLight` objects and `LightsNode.customCacheKey()` hashes only what is pushed, so parenting this
 * into the scene moves NO pipeline cache key — the same property `csmRig`'s point 2 establishes for
 * the cascade `LwLight`s, relied on here rather than newly claimed.
 *
 * ⚠️ AND THE MATRIX POLICY WOULD SILENTLY BREAK IT (point 4): `Object3D.DEFAULT_MATRIX_AUTO_UPDATE`
 * is false at `scene.ts` module scope, so a posed placeholder composes nothing and the box would
 * sit at the origin — shadows "from nowhere", no error.
 */
interface StaticHalf {
  light: Object3D & { castShadow: boolean; target: Object3D; shadow: THREE.DirectionalLightShadow };
  shadow: THREE.DirectionalLightShadow;
  node: unknown;
  /** The snapped anchor the box is currently built around; `null` until the first fit. */
  anchor: THREE.Vector3 | null;
  refits: number;
  dynamicObjects: number;
}

/** Scratch, module scope — the pose runs on a refit, but a per-refit allocation is still litter. */
const _dir = new Vector3();
const _centre = new Vector3();

export function createCsmRig(opts: CsmRigOptions): CsmRig {
  const { sun, camera, renderer, staticRoot } = opts;
  let policy = opts.policy;
  let wantCast = opts.castShadow;
  let node: CSMShadowNode | null = null;
  let applied = false;
  /**
   * The opaque per-probe sun-visibility node, or `null`. HELD across detach/attach so a bake
   * bracket, a cascade-count edit and a projection change all come back with the term still
   * installed — the volume's life is owned by `scene.ts`, not by the rig's swap schedule.
   */
  let distantTerm: Node<'float'> | null = null;
  /** Whether the node CURRENTLY in the slot is composed. A fact about the graph, not a wish. */
  let composed = false;
  let frustumRefits = 0;
  let keyCycles = 0;
  /** `null` = no swap pending; a number = the frame index the detach was observed on. */
  let pendingSince: number | null | undefined = undefined;
  let lastProjection = '';
  /**
   * ── ⭐ B6 · THE BAKE OVERRIDES THE SUN'S SCHEDULE, AND IT HAS TO ────────────────────────────────
   *
   * True for the whole `detachForBake` bracket. A bake photographs the world through the sun's own
   * ±extent map, so a `staticOnly` sun would hand the baker a shadow camera on
   * `FWG_STATIC_SHADOW_LAYER` — and every capture would be lit through a map containing only
   * whatever the last static-layer walk happened to have opted in. That is an atlas of a different
   * lighting rig with `checkProvenance` green over it, which is the failure class
   * `csmRig`'s point 6 already exists to prevent. Same shape as the bake bracket forcing
   * `previewStatic` false before it reads the light presences: the bake decides, and it restores.
   */
  let bakeDetached = false;
  /** R5: the static half, or `null` when the split is off / detached. */
  let staticHalf: StaticHalf | null = null;

  /*
   * ⚠️ QUANTISED, AND THE REASON IS A MEASUREMENT. The run-speed FOV kick (`scene.ts` `fovKickDeg`)
   * DAMPS `camera.fov` toward its target every frame while the player accelerates, so a key built
   * on the raw float differs every frame and `updateFrustums()` ran per frame: `frustumRefits`
   * climbed 1 → 95 in ~5 s of walking. Cascade splits do not need sub-degree fidelity — a 0.5°
   * bucket moves the near break by centimetres — so the key rounds fov to 0.5° and aspect to 1e-3.
   * `near`/`far` stay exact; they change on purpose or not at all.
   */
  const projectionKey = (): string =>
    `${Math.round(camera.fov * 2) / 2}|${Math.round(camera.aspect * 1000) / 1000}|${camera.near}|${camera.far}`;

  const shadowOf = (): ShadowWithNode => sun.shadow as unknown as ShadowWithNode;

  /**
   * ── ⭐ PUBLISH THE MAPS THIS RIG DRIVES, FOR THE VRAM CENSUS ────────────────────────────────────
   *
   * `vramAccountant.shadowBytesOf` used to duck-type into three's `CSMShadowNode` (`shadow.
   * shadowNode.lights[]`) to find the cascade maps, because the sun's own map is never allocated
   * under a custom shadow node. That reach fails the moment the slot holds a COMPOSITION — which it
   * does under the baked per-probe sun term (already, on main) and under the split — and the census
   * then silently prices the sun's own unallocated 2048² instead of what is really there.
   *
   * So the rig states it. Called from the two places the map set can change (`attachNow`,
   * `detachNow`) and nowhere else, and it lists EVERY map the sun's frame renders: the cascade
   * clones AND the static half. `state().split.staticMapPx` is the same fact from the other side.
   */
  function publishDrivenMaps(): void {
    const shadow = sun.shadow as unknown as Record<string, unknown>;
    const maps: THREE.LightShadow[] = [...sunShadowMaps()];
    if (staticHalf !== null) maps.push(staticHalf.shadow);
    // Detached (`?csm=0`, a frozen sun, mid-swap, inside a bake): the sun's OWN map is the live one
    // and the census's default path prices it correctly, so publish nothing rather than a list of
    // one that happens to agree.
    if (node === null && staticHalf === null) delete shadow[FWG_DRIVEN_SHADOW_MAPS];
    else shadow[FWG_DRIVEN_SHADOW_MAPS] = maps;
  }

  /**
   * ── ⭐ B6 · THE `LightShadow`s THE SUN'S FRAME ACTUALLY RENDERS ─────────────────────────────────
   *
   * ONE fact, and only this module holds it. Attached, it is the N cascade CLONES
   * (`CSMShadowNode._init`: `const lShadow = light.shadow.clone()` per cascade, each driven by its
   * own `ShadowNode` through `shadow( lwLight, lShadow )`) and the sun's own `shadow` is never
   * rendered at all — `AnalyticLightNode.setupShadow` takes `shadow.shadowNode` in place of
   * building it. Detached (`?csm=0`, a frozen sun, mid-swap, inside a bake bracket), it is the
   * sun's own single map.
   *
   * ⚠️ THE LIST IS READ FRESH ON EVERY USE, NEVER CACHED. `attachNow` builds new clones on a
   * two-frame swap and `detachNow` disposes them, so a handle held across a cascade-count edit
   * would schedule maps that no longer exist while the live ones silently stayed `everyFrame` —
   * a policy that reports itself applied and is not, over the light that costs the most passes.
   */
  const sunShadowMaps = (): THREE.LightShadow[] =>
    node === null
      ? [sun.shadow]
      : node.lights
          .map((lw) => lw.shadow as THREE.LightShadow | undefined)
          .filter((s): s is THREE.LightShadow => s !== undefined);

  /**
   * ── ⭐ B6 · PUT EVERY MAP THE SUN RENDERS INTO THE AUTHORED ARM ────────────────────────────────
   *
   * `prime` schedules ONE render of a frozen map — the `needsUpdate` half of `staticOnly`, which
   * `applyShadowUpdatePolicy` deliberately does not set because "the world changed" is knowledge it
   * does not have (see it). It is true on an attach and on `refreshSunShadow`, false on a plain
   * per-frame pass.
   *
   * ⚠️ EVERY MAP, OR NONE — a partially-frozen cascade set is a worse bug than an unfrozen one: the
   * near cascade would keep the player's shadow while the far ones froze, so shadows would appear
   * and vanish at the split distances with nothing on screen naming the cause. The loop is over
   * `sunShadowMaps()` for exactly that reason, and `state().shadowMapsDriven` is the count read
   * back off the objects.
   */
  function applySunShadowSchedule(prime: boolean): number {
    const update = bakeDetached ? 'everyFrame' : policy.shadowUpdate;
    const maps = sunShadowMaps();
    // The walk BEFORE the frozen maps render, and re-run rather than done once, for the reason
    // `worldLights.refreshCachedShadows` gives: the graph grew, and a mesh off the channel is a
    // wall missing from the map.
    if (update === 'staticOnly' && prime) enableStaticShadowLayer(staticRoot);
    for (const shadow of maps) {
      applyShadowUpdatePolicy(shadow, update);
      if (prime && update !== 'everyFrame') shadow.needsUpdate = true;
    }
    /*
     * ⭐ R5 · THE CASCADE LAYER MASKS ARE WRITTEN HERE, AFTER THE SCHEDULE, AND THAT ORDER IS
     * LOAD-BEARING. `applyShadowUpdatePolicy`'s `everyFrame` arm ends in
     * `shadow.camera.layers.set(0)` — deliberately, because a camera left holding two channels
     * fails r185's inherit test and silently stops rendering what the main camera can see. So the
     * split's masks must be applied AFTER it, in the one place that already loops these cameras,
     * or the schedule would quietly undo the split on every apply.
     *
     * `set`, not `enable`, for the same r185 reason: mask `1 << 4 = 16` passes
     * `16 & 0xFFFFFFFE ≠ 0` and is kept; a mask carrying layer 0 as well would be replaced by the
     * main camera's and the cascade would draw the whole world again — the split configured,
     * costing its VRAM, and doing nothing.
     */
    if (staticHalf !== null && !bakeDetached) {
      for (let i = policy.split.sharedCascades; i < maps.length; i++) {
        maps[i]!.camera.layers.set(FWG_DYNAMIC_SHADOW_LAYER);
      }
    }
    return update === 'everyFrame' ? 0 : maps.length;
  }

  /**
   * ── ⭐ R5 · FIT THE STATIC BOX TO THE CAMERA, SNAPPED — AND RE-RENDER ONLY WHEN IT MOVES ───────
   *
   * Returns true when the map was re-primed, which is the ONE thing this function costs: a full
   * depth pass over the static channel on the next frame. `CsmState.split.refits` is that count.
   *
   * The snap is what makes "follows the camera" and "frozen" both true: the anchor is quantised to
   * `refitM`, so ordinary movement inside a cell — and EVERY rotation, since the anchor is a
   * position — leaves the box exactly where it was and the map is not touched.
   */
  function fitStaticBox(force: boolean): boolean {
    const half = staticHalf;
    if (half === null) return false;
    const { staticRadiusM, refitM } = policy.split;
    const step = refitM;
    _centre.set(
      Math.round(camera.position.x / step) * step,
      Math.round(camera.position.y / step) * step,
      Math.round(camera.position.z / step) * step,
    );
    if (!force && half.anchor !== null && half.anchor.equals(_centre)) return false;
    (half.anchor ??= new Vector3()).copy(_centre);
    /*
     * The sun's own direction, read off the light rather than off the config — `applySun` is the
     * one writer of `sun.position` and the dome is aimed from the same vector, so reading the
     * object is what keeps this box aimed at the sun an artist can see. Degenerate (a sun at the
     * origin) falls back to straight down, which is a box rather than a NaN.
     */
    _dir.copy(sun.position);
    if (_dir.lengthSq() < 1e-9) _dir.set(0, 1, 0);
    _dir.normalize();
    // Stand off far enough that the whole box is in front of a `near` of 0.5 whatever the aim.
    const standoff = staticRadiusM * 2;
    half.light.position.copy(_centre).addScaledVector(_dir, standoff);
    half.light.target.position.copy(_centre);
    half.light.updateMatrix();
    half.light.target.updateMatrix();
    const cam = half.shadow.camera;
    cam.left = -staticRadiusM;
    cam.right = staticRadiusM;
    cam.top = staticRadiusM;
    cam.bottom = -staticRadiusM;
    cam.near = 0.5;
    cam.far = standoff + staticRadiusM;
    cam.updateProjectionMatrix();
    /*
     * The opt-IN walk, re-run on every refit rather than once, for `enableStaticShadowLayer`'s own
     * reason: the graph GROWS (a `static_mesh`'s 50 MB glTF lands seconds after its placement) and
     * a wall off the channel is a wall missing from the map. A refit is already paying a full pass,
     * so the walk rides a frame that is expensive anyway.
     */
    enableStaticShadowLayer(staticRoot);
    half.dynamicObjects = enableDynamicShadowLayer(staticRoot);
    half.shadow.needsUpdate = true;
    half.refits++;
    return true;
  }

  /** Build the static half. Idempotent per attach; `detachNow` is what takes it down. */
  function buildStaticHalf(split: SunShadowSplit): void {
    if (!split.enabled || staticHalf !== null) return;
    const light = new Object3D() as StaticHalf['light'];
    light.name = 'fwgStaticSunShadow';
    /*
     * ⭐ THE ONE PLACE IN `src/` WHERE THIS SPELLING IS FREE, AND THE SCAN IS RIGHT TO ASK.
     * `tests/renderLightShadowPolicy.test.ts` refuses a bare `<light>.castShadow =` because on a
     * real light it is in `LightsNode.customCacheKey()`'s per-light hash and therefore a full-scene
     * WGSL regeneration. This object is NOT `isLight`: `Renderer._projectObject` never pushes it,
     * so it never enters the light array the key is computed from. Written once at construction,
     * mirroring three's own non-light cascade objects (`CSMShadowNode.js:173`).
     */
    light.castShadow = true; // FWG_LIGHT_TOGGLE_OK: not a light, so in no cache key — see above
    light.target = new Object3D();
    // Point 4 — a posed object composes nothing under the repo's matrix policy.
    light.matrixAutoUpdate = true;
    light.target.matrixAutoUpdate = true;
    const shadow = sun.shadow.clone() as THREE.DirectionalLightShadow;
    if (shadow.mapSize.width !== split.staticMapSize) {
      shadow.mapSize.set(split.staticMapSize, split.staticMapSize);
    }
    shadow.camera.matrixAutoUpdate = true;
    /*
     * FROZEN, AND ON THE STATIC CHANNEL — both halves, or it is worse than useless
     * (`FWG_STATIC_SHADOW_LAYER`'s own note). This is the one map in this rig whose schedule is
     * NOT `policy.shadowUpdate`: it is `staticOnly` by construction, which is what the split IS,
     * so it is deliberately kept out of `sunShadowMaps()` and driven by `fitStaticBox` alone.
     */
    applyShadowUpdatePolicy(shadow, 'staticOnly');
    light.shadow = shadow;
    const parent = sun.parent;
    if (parent) {
      parent.add(light.target);
      parent.add(light);
    }
    staticHalf = {
      light,
      shadow,
      /*
       * ⚠️ THE `.d.ts` SAYS `Light`; r185's RUNTIME READS A POSE. `ShadowNode` touches its `light`
       * in exactly three places — `shadow.updateMatrices( light )` (which reads `light.matrixWorld`
       * and `light.target.matrixWorld`, `LightShadow.js:206-209`), `getShadowMaterial( light )`
       * (a WeakMap key) and `scene.name = 'Shadow Map [ ' + (light.name || light.id) + ' ]'`. It
       * never reads `isLight`, `color`, `intensity` or `dispose`. three's OWN `CSMShadowNode` relies
       * on this: its `LwLight extends Object3D` (`CSMShadowNode.js:25-35`) is handed to this same
       * factory at `:182`, and it is untyped only because that example ships as JS.
       *
       * ⭐ AND THE OBJECT MUST NOT BE A LIGHT, which is the point rather than a workaround: an
       * `isLight` object entering the graph would join `LightsNode.customCacheKey()`'s per-light
       * hash and regenerate every material's WGSL. The cast is narrow and named for that reason.
       */
      node: shadowNodeFactory(
        light as unknown as Parameters<typeof shadowNodeFactory>[0],
        shadow,
      ),
      anchor: null,
      refits: 0,
      dynamicObjects: 0,
    };
    fitStaticBox(true);
  }

  function disposeStaticHalf(): void {
    const half = staticHalf;
    if (half === null) return;
    half.light.parent?.remove(half.light);
    half.light.target.parent?.remove(half.light.target);
    // r185 disposes a `ShadowNode`'s render target through the node; the shadow object itself
    // holds no GPU handle of its own (`ShadowNode.setupRenderTarget` owns it).
    (half.node as { dispose?: () => void }).dispose?.();
    staticHalf = null;
  }

  /**
   * B6 — the per-frame half, and the only arm with one. `staticOnly` needs nothing here (that is
   * the point of it) and `everyFrame` is three's own default path.
   */
  function tickSunShadow(frameIndex: number): void {
    if (bakeDetached || policy.shadowUpdate !== 'everyNFrames') return;
    const maps = sunShadowMaps();
    for (let i = 0; i < maps.length; i++) {
      // Phase by CASCADE INDEX: three cascades all refreshing on the same frame is 3 × a full
      // depth pass on one frame in N and nothing on the others — the same total, delivered as a
      // hitch. Same rule, same function, as the local rig's phase by light index.
      if (shadowRefreshDue(frameIndex, i, policy.shadowUpdateN)) maps[i]!.needsUpdate = true;
    }
  }

  function attachNow(): void {
    if (!policy.enabled) return;
    const distanceM = Math.min(policy.distanceM, camera.far);
    const breaksM = policy.breaksM;
    const created = new CSMShadowNode(sun, {
      cascades: policy.cascades,
      maxFar: distanceM,
      lightMargin: policy.lightMarginM,
      mode: 'custom',
      // `far` arrives as `min( camera.far, maxFar )` = `distanceM` here, so the last break is 1.
      customSplitsCallback: (_cascades, _near, far, breaks) => {
        breaks.push(...cascadeBreaks(breaksM, far));
      },
    });
    created.fade = policy.fade;
    // Point 5: eager init with the GAME camera. `_init` builds the cascade objects and calls
    // `updateFrustums()` once.
    (created as unknown as CsmNodeInternals)._init({ camera, renderer });
    frustumRefits = 1;
    lastProjection = projectionKey();
    const cascadeFar = cascadeCameraFarM(distanceM, policy.lightMarginM);
    created.lights.forEach((lw, i) => {
      // Point 4: the matrix policy is opt-out; a posed object composes nothing on its own.
      lw.matrixAutoUpdate = true;
      lw.target.matrixAutoUpdate = true;
      const shadow = lw.shadow;
      if (!shadow) return;
      const size = policy.mapSizes[i] ?? policy.mapSizes[0] ?? shadow.mapSize.width;
      if (shadow.mapSize.width !== size) shadow.mapSize.set(size, size);
      // `applySun`'s far is `max( 500, distance + 3·extent )` for a camera AT the sun; the cascade
      // camera is posed `lightMargin` past its slice, so its far is derived from THAT pose.
      shadow.camera.far = cascadeFar;
      shadow.camera.matrixAutoUpdate = true;
      shadow.camera.updateProjectionMatrix();
    });
    node = created;
    applied = true;
    // R5: BEFORE the composition below (it takes the `min` against this node) and before
    // `applySunShadowSchedule` (which reads `staticHalf` to decide the cascade layer masks).
    // Inside `attachNow` rather than at its call sites so a fourth one cannot forget it, the same
    // argument the schedule line at the bottom of this function carries.
    if (!bakeDetached) buildStaticHalf(policy.split);
    /*
     * ⭐ THE FORK IS HERE, IN JS, ONCE PER ATTACH — AND IT IS NOT R1.6-D.
     *
     * R1.6-D is two program variants sharing ONE pipeline-cache entry because the fork is invisible
     * to the cache key. This fork is accompanied by a `castShadow` cycle, and three independent
     * r185 mechanisms guarantee the two variants never share an entry: `LightsNode.customCacheKey()`
     * hashes `light.castShadow` per light, so the key genuinely moves; `NodeManager.delete`
     * evicts the pre-swap `nodeBuilderCache` entry when its `usedTimes` reaches 0; and
     * `AnalyticLightNode.setup`'s `castShadow === false` arm disposes the light node's own cached
     * `shadowColorNode`. It is the SAME machinery trap 4 and `detachForBake` already rely on and
     * pin — proven, not newly claimed.
     *
     * The `null` arm assigns the bare node: the same object, the same graph, byte-identical WGSL,
     * zero added instructions. `tests/renderSparseAddress.test.ts` asserts that node-for-node
     * rather than reviewing it.
     */
    composed = distantTerm !== null;
    const dynamicTerm = composed
      ? composeDistantShadow(created, distantTerm!, camera, policy.fade)
      : created;
    /*
     * ── ⭐ R5 · `min( static, dynamic )` — AND THE REASON IT IS SAFE IS A PROPERTY OF r185 ────────
     *
     * `ShadowNode.setupShadowFilter` ends in `frustumTest.select( shadowNode, float( 1 ) )`
     * (`ShadowNode.js:321-329`), so **a shadow node returns exactly 1 outside its own box**. The
     * static term is therefore the IDENTITY of `min` everywhere it has nothing to say: beyond the
     * static box the composed value is the cascade term, byte for byte what ships today. There is
     * no black ring at the box edge and no arm in which this can darken a frame it does not cover
     * — which is the property that makes a second shadow map composable at all.
     *
     * `min` rather than a product because occlusion composes by "either occluder darkens", and the
     * two terms are over DISJOINT object sets (`FWG_DYNAMIC_SHADOW_LAYER`'s note: one mark decides
     * both channels), so no caster is counted twice. A product would darken the union of two
     * penumbrae; `min` gives the darker of the two, which is what a shadow is.
     *
     * ⚠️ THE ONE OPEN COMPOSITION QUESTION, NAMED RATHER THAN HIDDEN. When a world carries BOTH the
     * split and a baked per-probe sun term, `dynamicTerm` already fades toward `V` past 67.5 m and
     * the static map covers to `staticRadiusM` — so inside that overlap two authorities describe
     * the same static occlusion at very different resolutions (11 cm texels against 4.8 m probe
     * spacing on `mars-hangar-01`). `min` takes the darker, which is conservative and never black,
     * but where they disagree the frame is darker than either alone. It is composed rather than
     * refused because refusing would silently remove a shipped feature under a flag; it has NOT
     * been looked at on a real frame, and that is the first thing to look at on a GI world.
     */
    shadowOf().shadowNode =
      staticHalf !== null
        ? min(
            nodeObject(staticHalf.node as Node<'float'>),
            nodeObject(dynamicTerm as Node<'float'>),
          )
        : dynamicTerm;
    // The attach takes effect at the next build; `castShadow` is the key move that forces one.
    sun.castShadow = wantCast; // FWG_LIGHT_TOGGLE_OK: the CSM attach — the ONE key move, by design
    publishDrivenMaps();
    // B6: the clones were copied from `sun.shadow` (which carries `applySun`'s arm already), but
    // they are copied ONCE at `_init` and this rig outlives that — a `shadowUpdate` edit after an
    // attach reaches the clones only here. Inside `attachNow` rather than at its three call sites
    // so a fourth one cannot forget it.
    applySunShadowSchedule(true);
  }

  function detachNow(): void {
    shadowOf().shadowNode = undefined;
    composed = false;
    // R5: the static half goes with the cascades, always. It is an OPERAND of the node in the
    // slot, so leaving it alive across a swap would orphan a 33.6 MB render target and leave a
    // second `updateBefore` registered against a graph nothing samples.
    disposeStaticHalf();
    if (node) {
      // Removes the cascade objects from the sun's parent and frees the node.
      node.dispose();
      node = null;
      // The census must not keep pricing maps that no longer exist. Inside the `if` is wrong —
      // `disposeStaticHalf` above can have changed the set on its own — so it is re-published
      // unconditionally below.
    }
    applied = false;
    publishDrivenMaps();
  }

  attachNow();
  // B6: and again unconditionally, because `attachNow` returns early when the policy is not
  // enabled (`?csm=0`, or a `staticOnly` sun — `sunShadowPolicy` resolves that arm to the single
  // world-anchored map on purpose). In that arm the sun's OWN map is the live one and it still has
  // to be primed; a schedule that only worked while cascades were attached would be a lever that
  // silently did nothing on exactly the arm that asks for it.
  applySunShadowSchedule(true);

  return {
    policy: () => policy,
    tick(frameIndex) {
      // B6 FIRST, and outside the `enabled` guard: under `?csm=0` or a frozen sun there is no node
      // and this rig is still the only thing that owns the sun's shadow schedule.
      tickSunShadow(frameIndex);
      if (!policy.enabled) return;
      if (pendingSince === null) {
        // First tick after a scheduled detach: this frame's draw is the `castShadow === false`
        // build that disposes the cached shadow term. Attach on the next one.
        pendingSince = frameIndex;
        return;
      }
      if (pendingSince !== undefined && frameIndex > pendingSince) {
        pendingSince = undefined;
        attachNow();
        keyCycles++;
        return;
      }
      if (!node) return;
      /*
       * ⭐ R5 — the static box's only per-frame cost, and it is a snap plus a compare. `fitStaticBox`
       * re-primes the map ONLY when the quantised anchor moves, so standing still and every camera
       * ROTATION are free by construction (the anchor is a position). Not gated on `bakeDetached`
       * because `staticHalf` is null for the whole bracket.
       */
      fitStaticBox(false);
      const key = projectionKey();
      if (key !== lastProjection) {
        lastProjection = key;
        node.updateFrustums();
        frustumRefits++;
      }
    },
    apply(next, castShadow) {
      wantCast = castShadow;
      const rebuild =
        policy.enabled !== next.enabled ||
        policy.cascades !== next.cascades ||
        policy.fade !== next.fade ||
        // R5: both of these change the NODE GRAPH — `enabled` adds or removes the `min` operand,
        // and `sharedCascades` is the only thing deciding which cascade cameras take the dynamic
        // channel, which `applySunShadowSchedule` writes from `attachNow`. A free-write path here
        // would leave the composed graph describing a split the cameras are not rendering.
        policy.split.enabled !== next.split.enabled ||
        policy.split.sharedCascades !== next.split.sharedCascades;
      /*
       * ⭐ B6: A SCHEDULE CHANGE IS NOT A REBUILD. `autoUpdate`, `camera.layers` and `needsUpdate`
       * are all in `LIGHT_KEY_FREE_FIELDS` — in no `LightsNode.customCacheKey()` and in no
       * `NodeManager.getCacheKey` — so an artist switching the sun's arm rebuilds not one
       * material's WGSL, which is the whole reason the lever is a schedule and not a `castShadow`
       * toggle. It is applied unconditionally BEFORE the `rebuild`/`!node` early returns: those two
       * exist to skip the CASCADE writes, and the schedule has to reach the single-map arm too.
       * (`staticOnly` moves `enabled`, so that switch takes the `rebuild` path and re-primes
       * through `attachNow`/this line.)
       */
      const scheduleMoved =
        policy.shadowUpdate !== next.shadowUpdate || policy.shadowUpdateN !== next.shadowUpdateN;
      policy = next;
      if (!rebuild) applySunShadowSchedule(scheduleMoved);
      if (rebuild) {
        detachNow();
        if (next.enabled) {
          // Two-frame swap: hold the sun's flag false through the next draw so the light node
          // disposes its cached term; `tick` attaches after that draw.
          pendingSince = null;
          sun.castShadow = false; // FWG_LIGHT_TOGGLE_OK: first half of a cascade-count swap
        } else {
          sun.castShadow = wantCast; // FWG_LIGHT_TOGGLE_OK: `?csm=0`-style disable back to the stock map
          // B6: the cascades are gone, so the sun's OWN map is the live one from this frame on and
          // it takes the arm here. This is the path a `staticOnly` edit lands on — `sunShadowPolicy`
          // resolves that arm to the single world-anchored map, so it always moves `enabled`.
          applySunShadowSchedule(true);
        }
        return;
      }
      if (!node) {
        /*
         * ── ⚠️ FOUND BY MEASUREMENT: A NON-REBUILD `apply` USED TO CANCEL A PENDING SWAP ──────────
         *
         * `pendingSince !== undefined` means `detachNow()` has just run and `sun.castShadow` is
         * being HELD false through the next draw on purpose — that build is what makes
         * `AnalyticLightNode.setup` dispose the light node's cached `shadowColorNode` (trap 4).
         * Re-raising the flag here cancels exactly that: the light node never sees a
         * `castShadow === false` build, keeps the PRE-swap term, and `attachNow` then installs a
         * new node into a slot nothing reads.
         *
         * ⭐ THE OBSERVABLE IS A PASS COUNT, AND IT IS WHY THIS WAS FOUND AT ALL. Driving the rig
         * `3 cascades → ?csm=2 → back to 3` and then any second apply in the same turn left the
         * frame rendering **2 cascade passes while `state().cascades` reported 3** — the policy and
         * the graph disagreeing, silently, with every readout green. Two applies in one turn is not
         * exotic: it is what the editor does on a light edit after a cascade change, and it is what
         * any harness that resets several session doors before setting one does.
         *
         * `undefined` = nothing pending; `null` = detached and awaiting the first tick; a number =
         * awaiting the second. Only the first may re-assert the flag.
         */
        if (pendingSince === undefined) {
          sun.castShadow = wantCast; // FWG_LIGHT_TOGGLE_OK: `applySun` already wrote it; re-asserted
        }
        return;
      }
      // Free writes: reach and map sizes. The count is unchanged so the graph is unchanged.
      const distanceM = Math.min(next.distanceM, camera.far);
      node.maxFar = distanceM;
      const cascadeFar = cascadeCameraFarM(distanceM, next.lightMarginM);
      node.lights.forEach((lw, i) => {
        const shadow = lw.shadow;
        if (!shadow) return;
        const size = next.mapSizes[i] ?? next.mapSizes[0] ?? shadow.mapSize.width;
        if (shadow.mapSize.width !== size) shadow.mapSize.set(size, size);
        shadow.camera.far = cascadeFar;
      });
      node.updateFrustums();
      frustumRefits++;
      sun.castShadow = wantCast; // FWG_LIGHT_TOGGLE_OK: the authored flag, re-asserted after `applySun`
    },
    detachForBake(renderOnce) {
      /*
       * ⭐ B6: THE SCHEDULE IS FORCED FIRST, AND FOR A WORLD WITH NO CASCADES AT ALL THIS IS THE
       * WHOLE BRACKET. The old `!policy.enabled` early return was correct while the only way to
       * reach it was `?csm=0` — a dev arm nobody bakes on. A `staticOnly` sun reaches it from a
       * FILE, and baking through a frozen, static-layer-restricted shadow camera would photograph
       * ~500,000 captures through a shadow map of whatever the last walk opted in. So: force
       * `everyFrame`, hand the baker a live world-anchored map, restore the authored arm after.
       */
      bakeDetached = true;
      applySunShadowSchedule(false);
      if (!policy.enabled) {
        return () => {
          bakeDetached = false;
          applySunShadowSchedule(true);
        };
      }
      // Always the full cycle, even mid-swap: a pending attach must not fire inside the bake, and
      // a light node that never saw a `castShadow === false` build still holds the cascade term.
      pendingSince = undefined;
      detachNow();
      sun.castShadow = false; // FWG_LIGHT_TOGGLE_OK: bake detach, half 1 — disposes the cached CSM term
      renderOnce();
      sun.castShadow = wantCast; // FWG_LIGHT_TOGGLE_OK: bake detach, half 2 — the stock map builds at the first capture
      keyCycles++;
      return () => {
        sun.castShadow = false; // FWG_LIGHT_TOGGLE_OK: bake restore, half 1 — disposes the stock term
        renderOnce();
        // B6: BEFORE `attachNow`, which primes the new clones — a restore that left the override on
        // would attach the cascades in `everyFrame` and the authored arm would silently be gone
        // until the next `apply`, i.e. for the rest of the session on a world nobody edits again.
        bakeDetached = false;
        attachNow(); // half 2: `castShadow` back to the authored value with the cascades installed
        keyCycles++;
      };
    },
    /**
     * ── ⭐ INSTALL THE BAKED PER-PROBE SUN VISIBILITY AS THE SUN'S DISTANT SHADOW TERM ──────────
     *
     * ONE WRITER. `sun.shadow.shadowNode` is written in exactly two places in this file
     * (`attachNow`, `detachNow`) and nowhere else in `src/` — scanned, not reviewed
     * (`tests/renderCsm.test.ts`). This function does not write it; it records the term and asks
     * `attachNow` for a graph, which is what keeps the composition and the CSM node's lifetime in
     * one place.
     *
     * ⭐ **IT IS A KEY MOVE, DELIBERATELY, AND IT IS SPENT ONCE PER BIND.** The graph changes, so
     * the two-frame `castShadow` swap is mandatory: `LightsNode.setupLightsNode` keeps one
     * `AnalyticLightNode` per light in a module WeakMap for the page's life and it caches
     * `shadowColorNode` until a build with `castShadow === false` disposes it (trap 4). Without the
     * cycle the term would be assigned and never appear, which is this repo's signature failure
     * shape — a subsystem that compiles, costs and does nothing.
     *
     * ⚠️ **AND `scene.ts` MUST CALL THIS BEFORE `scene.add(volume)`, WHICH MAKES THE CYCLE FREE.**
     * Adding the `SparseProbeVolume` puts a new `light.id` into `LightsNode.customCacheKey()`'s
     * hash and therefore already forces a full-scene rebuild at bind time; ordering our swap ahead
     * of it lands both inside one rebuild wave instead of two. `state().keyCycles` and the
     * pipeline count across a bind are the witnesses.
     *
     * Three arms, and the first two are silent on purpose:
     *   - **the same node** (a republish onto the same light) — an identity check, no cycle, no
     *     rebuild. Without it a re-bake pickup would spend a full-scene recompile to install the
     *     node it already has.
     *   - **detached** (inside `detachForBake`, or after `dispose`) — record only. `restoreCsm`'s
     *     `attachNow` composes it on the way out; arming a pending attach here would fire a SECOND
     *     `attachNow` after the restore and orphan the first node's cascade objects.
     *   - **attached** — the swap, exactly as a cascade-count edit takes it.
     *
     * ⛔ **`?csm=0` GETS NOTHING, AND THAT IS A REFUSAL RATHER THAN AN OVERSIGHT.** With
     * `policy.enabled === false` the sun keeps `applySun`'s stock ±`shadowExtentM` box and this rig
     * owns no node to compose into. Building one would mean csmRig re-implementing three's own
     * shadow node so it had something to multiply — a second answer to "what is the sun's shadow",
     * in the one arm that exists to be the control for measuring the first. The term is recorded
     * (so enabling cascades later picks it up) and `state().distantTerm` reads `'none'`, which is
     * the honest report.
     */
    setDistantShadowTerm(term) {
      if (term === distantTerm) return;
      distantTerm = term;
      if (!policy.enabled) return;
      // Detached with nothing scheduled ⇒ a bake bracket or a disposed rig: record only.
      if (node === null && pendingSince === undefined) return;
      detachNow();
      pendingSince = null;
      sun.castShadow = false; // FWG_LIGHT_TOGGLE_OK: first half of the distant-term swap — the light node must see a castShadow===false build to drop its cached term
    },
    refreshSunShadow() {
      // R5: "the world changed" is exactly the moment the static map is a picture of a level that
      // is gone — the same sentence this method's header makes about a frozen sun. `force` because
      // the anchor has not moved: the CONTENT under it has.
      fitStaticBox(true);
      return applySunShadowSchedule(true);
    },
    state() {
      /*
       * B6 — READ OFF THE `LightShadow`s, not off the policy. `autoUpdate === false` is what
       * `ShadowNode.updateBefore` actually gates on (`shadow.needsUpdate || shadow.autoUpdate`),
       * so this counts maps whose pass is genuinely under this rig's control. A count that
       * disagrees with `cascades` under a non-`everyFrame` arm is the partially-frozen cascade set,
       * visible as a number rather than as an intermittent shadow at a split distance.
       */
      const driven = sunShadowMaps().filter((s) => s.autoUpdate === false).length;
      return {
        enabled: policy.enabled,
        attached: node !== null,
        cascades: policy.enabled ? policy.cascades : 0,
        cascadesAttached: node ? node.lights.length : 0,
        distanceM: policy.enabled ? Math.min(policy.distanceM, camera.far) : 0,
        breaksM: [...policy.breaksM],
        mapSizes: [...policy.mapSizes],
        fade: policy.fade,
        cameraBound: node !== null && node.camera === camera,
        lightsParented: node ? node.lights.filter((l) => l.parent !== null).length : 0,
        applied,
        pendingAttach: pendingSince !== undefined,
        frustumRefits,
        keyCycles,
        // The GRAPH's answer, not the intention: `distantTerm` can be held while `composed` is
        // false (inside a bake bracket, mid-swap, under `?csm=0`), and reporting the wish there
        // would be a self-report that disagrees with the frame.
        distantTerm: composed ? ('probe' as const) : ('none' as const),
        handoverBandM: sunHandoverBandM(policy),
        shadowUpdate: bakeDetached ? ('everyFrame' as const) : policy.shadowUpdate,
        shadowUpdateN: policy.shadowUpdateN,
        shadowMapsDriven: driven,
        /*
         * R5 — READ OFF THE OBJECTS, not off `policy.split`. `dynamicCascades` counts cameras whose
         * mask IS the dynamic channel and `staticLayerMask` is the static camera's own mask, so a
         * split whose layer writes were undone (by a schedule re-apply, by a future caller of
         * `applyShadowUpdatePolicy`) reads as un-applied here instead of as configured. That is the
         * whole reason this block exists: the split's failure mode is a picture.
         */
        split: {
          requested: policy.split.enabled,
          attached: staticHalf !== null,
          sharedCascades: policy.split.sharedCascades,
          dynamicCascades: sunShadowMaps().filter(
            (s) => s.camera.layers.mask === 1 << FWG_DYNAMIC_SHADOW_LAYER,
          ).length,
          dynamicObjects: staticHalf?.dynamicObjects ?? 0,
          frozenFromM: staticHalf ? policy.split.frozenFromM : 0,
          staticRadiusM: staticHalf ? policy.split.staticRadiusM : 0,
          staticCentre: staticHalf?.anchor
            ? [staticHalf.anchor.x, staticHalf.anchor.y, staticHalf.anchor.z]
            : [0, 0, 0],
          texelM:
            staticHalf && policy.split.staticMapSize > 0
              ? (2 * policy.split.staticRadiusM) / policy.split.staticMapSize
              : 0,
          refits: staticHalf?.refits ?? 0,
          staticLayerMask: staticHalf ? staticHalf.shadow.camera.layers.mask : 0,
          staticMapPx: staticHalf ? staticHalf.shadow.mapSize.width : 0,
          staticAutoUpdate: staticHalf ? staticHalf.shadow.autoUpdate : false,
          staticNeedsUpdate: staticHalf ? staticHalf.shadow.needsUpdate : false,
        },
      };
    },
    dispose() {
      pendingSince = undefined;
      detachNow();
    },
  };
}
